/**
 * features/compliance/ComplianceItemEditor.tsx
 * Add or edit a compliance record. For new records, pick a template
 * (Tax Clearance, Incorporation, …); the expiry date is suggested
 * from the issue date and the template's validity but stays editable.
 * A copy of the document can be scanned or uploaded.
 */
import { useState } from 'react';
import { X, ShieldCheck, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { glassSurface } from '@/components/ui/glass';
import { Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { ReceiptCapture } from '@/features/common/ReceiptCapture';
import type { ComplianceItem, ComplianceKind } from '@/types';
import { COMPLIANCE_TEMPLATES, COMPLIANCE_LIST, addMonths } from './complianceMeta';
import { addComplianceItem, updateComplianceItem, deleteComplianceItem } from './complianceWrite';

export function ComplianceItemEditor({ item, onClose }: { item: ComplianceItem | null; onClose: () => void }) {
  const { user } = useAuth();
  const actor = user?.email ?? user?.uid ?? 'unknown';

  const [kind, setKind] = useState<ComplianceKind | null>(item?.kind ?? null);
  const [title, setTitle] = useState(item?.title ?? '');
  const [reference, setReference] = useState(item?.reference ?? '');
  const [authority, setAuthority] = useState(item?.authority ?? '');
  const [issueDate, setIssueDate] = useState(item?.issueDate ?? '');
  const [expiryDate, setExpiryDate] = useState(item?.expiryDate ?? '');
  const [expiryEdited, setExpiryEdited] = useState(!!item?.expiryDate);
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(item?.attachmentUrl ?? null);
  const [attachmentName, setAttachmentName] = useState<string | undefined>(item?.attachmentName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const template = kind ? COMPLIANCE_TEMPLATES[kind] : null;

  function pick(k: ComplianceKind) {
    const t = COMPLIANCE_TEMPLATES[k];
    setKind(k);
    setTitle(t.label);
    setAuthority(t.authority ?? '');
  }

  function onIssueChange(v: string) {
    setIssueDate(v);
    if (v && template?.expiryMonths && !expiryEdited) {
      setExpiryDate(addMonths(v, template.expiryMonths));
    }
  }

  async function save() {
    if (!title.trim()) {
      setError('A title is required.');
      return;
    }
    setBusy(true);
    setError('');
    const payload = {
      kind: kind ?? 'other',
      title,
      reference,
      authority,
      issueDate,
      expiryDate,
      notes,
      attachmentUrl: attachmentUrl ?? undefined,
      attachmentName,
    };
    try {
      if (item) await updateComplianceItem(item.id, payload);
      else await addComplianceItem(payload, actor);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
      setBusy(false);
    }
  }

  async function remove() {
    if (!item || !window.confirm('Delete this record?')) return;
    setBusy(true);
    try {
      await deleteComplianceItem(item.id);
      onClose();
    } catch {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
      <div className="my-10 w-full max-w-lg">
        <div className={glassSurface('space-y-4 p-5')}>
          <div className="-mx-5 -mt-5 mb-1 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
            <div className="flex items-center gap-2 text-white">
              <ShieldCheck size={18} />
              <h2 className="text-base font-semibold text-white">{item ? 'Edit record' : 'Add compliance record'}</h2>
            </div>
            <button onClick={onClose} className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white" aria-label="Close">
              <X size={18} />
            </button>
          </div>

          {!kind && !item ? (
            <div className="grid gap-2 sm:grid-cols-2">
              {COMPLIANCE_LIST.map((t) => (
                <button
                  key={t.kind}
                  onClick={() => pick(t.kind)}
                  className="rounded-xl border border-black/10 p-3 text-left transition hover:border-brand hover:bg-brandSoft/50 dark:border-white/10 dark:hover:bg-white/5"
                >
                  <div className="font-semibold text-primary dark:text-gray-100">{t.label}</div>
                  <div className="mt-0.5 text-xs text-primaryLight dark:text-gray-400">{t.blurb}</div>
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field label="Title">
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} />
                  </Field>
                </div>
                <Field label="Reference / certificate no.">
                  <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. TCC/26-27/002975" />
                </Field>
                <Field label="Authority">
                  <Input value={authority} onChange={(e) => setAuthority(e.target.value)} placeholder="BURS / CIPA" />
                </Field>
                <Field label="Issue date">
                  <Input type="date" value={issueDate} onChange={(e) => onIssueChange(e.target.value)} />
                </Field>
                <Field label="Expiry / renewal date" hint={template?.expiryMonths ? 'Auto-filled from issue date.' : undefined}>
                  <Input
                    type="date"
                    value={expiryDate}
                    onChange={(e) => {
                      setExpiryDate(e.target.value);
                      setExpiryEdited(true);
                    }}
                  />
                </Field>
              </div>

              <Field label="Notes">
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>

              <Field label="Document" hint="Scan or upload a copy to download later.">
                <ReceiptCapture
                  value={attachmentUrl ?? undefined}
                  onChange={(url, name) => {
                    setAttachmentUrl(url);
                    setAttachmentName(name);
                  }}
                  folder="complianceDocs"
                  scanLabel="Scan document"
                  uploadLabel="Upload PDF / image"
                  viewLabel={attachmentName ? `View ${attachmentName}` : 'View document'}
                />
              </Field>

              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

              <div className="flex items-center justify-between gap-2">
                <div>
                  {item && (
                    <Button variant="ghost" icon={<Trash2 size={16} />} onClick={remove} disabled={busy}>
                      Delete
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button onClick={save} loading={busy}>
                    Save record
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
